/**
* @summary - Component that Provides Additional Info Tab
* @author Yuvasri S
* @since 2024-07-04
*/
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Box, Button, IconButton, Typography } from '@mui/material';
import TabContentLayout from './TabContentLayout';
import CustomTextField from '../commoncomponents/CustomTextField';
import { getAuthToken, getcompanyId, getCreatedBy } from "../../utility/tokenUtility";
import { MasterGraph, ifacSecurityURL } from '../../utility/apiURLS';
import axios from "axios";
import { updateAdditionalInfo } from '../../redux/reducers/HospitalInformationReducer/formSlice';
import { BreadcrumbBar } from '../../assets/styled-components/breadcrumbBar';
import HomeIconButton from '../commoncomponents/HomeIconButton';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { DefaultCard } from '../../assets/styled-components/card';
import { useNavigate } from "react-router";
import CustomDialog from '../commoncomponents/CustomDialog';

function AdditionalInfoContent({ hospitalcode, hospitalname, hospitalInformation, additionalInfo, onUpdateAdditionalInfo, isViewMode, isEditMode }) {
    const dispatch = useDispatch();
    const hospitalDetailsId = useSelector((state) => state.form.mashospitaldetailsId);
    const [isUpdate, setIsUpdate] = useState(false);
    const [primaryId, setPrimaryId] = useState('')
    const { landarea, clsarea, bedstrength, groundmaint, llssoilfactor, llsringgitfactor } = additionalInfo;
    const {
        id,
    } = hospitalInformation;
    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    const companyId = getcompanyId('companyId');
    const createdBy = getCreatedBy('createdBy');
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [succesDialogMessage, setSuccessDialogMessage] = useState('');

    const [errors, setErrors] = useState({
        landarea: false, clsarea: false, groundmaint: false, llssoilfactor: false, llsringgitfactor: false
    });

    const [errorMessages, setErrorMessages] = useState({
        landarea: '', clsarea: '', groundmaint: '', llssoilfactor: '', llsringgitfactor: ''
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);


    const fetchAdditionalInfo = async () => {
        if (!hospitalDetailsId) return;
        try {
            const response = await axios.get(`${MasterGraph}/api/GraphHospitalMaster/GetMashospitaladdlinfoAsyncFetch/${hospitalDetailsId}`, config);

            // Check if response data is valid and contains expected data
            if (response.data && response.data.length > 0) {
                const additionalInfo = response.data[0]; // Assuming the API returns an array and we need the first item
                // Update the state with the fetched details
                onUpdateAdditionalInfo({
                    landarea: additionalInfo.landarea,
                    clsarea: additionalInfo.cleansingarea,
                    bedstrength: additionalInfo.bedstrength,
                    groundmaint: additionalInfo.groundmaintarea,
                    llssoilfactor: additionalInfo.llssoilfactor,
                    llsringgitfactor: additionalInfo.llsringgitfactor
                });

                if (additionalInfo.id) {
                    setIsUpdate(true);
                    setPrimaryId(additionalInfo.id);
                }
            } else {
                console.error("No Additional Info found for the given hospital details ID");
                setDialogOpen(true);
                // Handle the case where no facility details are found
                onUpdateAdditionalInfo({
                    landarea: '',
                    clsarea: '',
                    bedstrength: '',
                    groundmaint: '',
                    llssoilfactor: '',
                    llsringgitfactor: ''
                });
            }
        } catch (error) {
            console.error("Error fetching Addtional Info:", error);

            // Handle the error by resetting the state or showing an error message
            onUpdateAdditionalInfo({
                landarea: '',
                clsarea: '',
                bedstrength: '',
                groundmaint: '',
                llssoilfactor: '',
                llsringgitfactor: ''
            });
        }
    };


    const handleChange = (field, newValue) => {

        onUpdateAdditionalInfo({ [field]: newValue });
        setHasUnsavedChanges(true); // Flagging that there are unsaved changes
        switch (field) {
            case 'landarea':
                dispatch(updateAdditionalInfo({ landarea: newValue }));
                if (/^\d*\.?\d*$/.test(newValue)) {
                    setErrors((prevErrors) => ({ ...prevErrors, landarea: false }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, landarea: '' }));
                } else {
                    setErrors((prevErrors) => ({ ...prevErrors, landarea: true }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, landarea: 'Please enter a valid number' }));
                }
                break;
            case 'clsarea':
                dispatch(updateAdditionalInfo({ clsarea: newValue }));
                if (/^\d*\.?\d*$/.test(newValue)) {
                    setErrors((prevErrors) => ({ ...prevErrors, clsarea: false }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, clsarea: '' }));
                } else {
                    setErrors((prevErrors) => ({ ...prevErrors, clsarea: true }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, clsarea: 'Please enter a valid number' }));
                }
                break;
            case 'bedstrength':
                dispatch(updateAdditionalInfo({ bedstrength: newValue }));
                if (/^\d*\.?\d*$/.test(newValue)) {
                    setErrors((prevErrors) => ({ ...prevErrors, bedstrength: false }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, bedstrength: '' }));
                } else {
                    setErrors((prevErrors) => ({ ...prevErrors, bedstrength: true }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, bedstrength: 'Please enter a valid number' }));
                }
                break;
            case 'groundmaint':
                dispatch(updateAdditionalInfo({ groundmaint: newValue }));

                if (/^\d*\.?\d*$/.test(newValue)) {
                    setErrors((prevErrors) => ({ ...prevErrors, groundmaint: false }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, groundmaint: '' }));
                } else {
                    setErrors((prevErrors) => ({ ...prevErrors, groundmaint: true }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, groundmaint: 'Please enter a valid number' }));
                }
                break;
            case 'llssoilfactor':
                dispatch(updateAdditionalInfo({ llssoilfactor: newValue }));
                if (/^\d*\.?\d*$/.test(newValue)) {
                    setErrors((prevErrors) => ({ ...prevErrors, llssoilfactor: false }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, llssoilfactor: '' }));
                } else {
                    setErrors((prevErrors) => ({ ...prevErrors, llssoilfactor: true }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, llssoilfactor: 'Please enter a valid number' }));
                }
                break;
            case 'llsringgitfactor':
                dispatch(updateAdditionalInfo({ llsringgitfactor: newValue }));
                if (/^\d*\.?\d*$/.test(newValue)) {
                    setErrors((prevErrors) => ({ ...prevErrors, llsringgitfactor: false }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, llsringgitfactor: '' }));
                } else {
                    setErrors((prevErrors) => ({ ...prevErrors, llsringgitfactor: true }));
                    setErrorMessages((prevMessages) => ({ ...prevMessages, llsringgitfactor: 'Please enter a valid number' }));
                }
                break;
            default:
                break;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        let formIsValid = true;
        const newErrors = { landarea: false, clsarea: false, groundmaint: false, llssoilfactor: false, llsringgitfactor: false };
        const newErrorMessages = { landarea: '', clsarea: '', groundmaint: '', llssoilfactor: '', llsringgitfactor: '' };

        // Validate landarea
        if (landarea && !/^\d*\.?\d*$/.test(landarea)) {
            formIsValid = false;
            newErrors.landarea = true;
            newErrorMessages.landarea = 'Please enter a valid number';
        }
        // Validate clsarea
        if (clsarea && !/^\d*\.?\d*$/.test(clsarea)) {
            formIsValid = false;
            newErrors.clsarea = true;
            newErrorMessages.clsarea = 'Please enter a valid number';
        }
        // Validate groundmaint
        if (groundmaint && !/^\d*\.?\d*$/.test(groundmaint)) {
            formIsValid = false;
            newErrors.groundmaint = true;
            newErrorMessages.groundmaint = 'Please enter a valid number';
        }
        // Validate bedstrength
        if (bedstrength && !/^\d*\.?\d*$/.test(bedstrength)) {
            formIsValid = false;
            newErrors.bedstrength = true;
            newErrorMessages.bedstrength = 'Please enter a valid number';
        }
        // Validate llssoilfactor
        if (llssoilfactor && !/^\d*\.?\d*$/.test(llssoilfactor)) {
            formIsValid = false;
            newErrors.llssoilfactor = true;
            newErrorMessages.llssoilfactor = 'Please enter a valid number';
        }
        // Validate llsringgitfactor
        if (llsringgitfactor && !/^\d*\.?\d*$/.test(llsringgitfactor)) {
            formIsValid = false;
            newErrors.llsringgitfactor = true;
            newErrorMessages.llsringgitfactor = 'Please enter a valid number';
        }

        setErrors(newErrors);
        setErrorMessages(newErrorMessages);

        if (formIsValid) {
            handleSave();
            setHasUnsavedChanges(false);
        }
    };

    const handleSave = async () => {
        try {
            // Determine whether to make a POST or PUT request
            if (primaryId) {
                const payload = {
                    mashospitaldetailsId: hospitalDetailsId,
                    createdby: createdBy,
                    landarea: landarea,
                    cleansingarea: clsarea,
                    bedstrength: bedstrength,
                    groundmaintarea: groundmaint,
                    llssoilfactor: llssoilfactor,
                    llsringgitfactor: llsringgitfactor,
                    id: primaryId
                };
                // PUT request
                const putUrl = `${MasterGraph}/api/mashospitaladdlinfos/${primaryId}`;
                await axios.put(putUrl, payload, config);
                setSuccessDialogMessage('Data updated successfully.');
            } else {
                const payload = {
                    createdby: createdBy,
                    landarea: landarea,
                    cleansingarea: clsarea,
                    bedstrength: bedstrength,
                    groundmaintarea: groundmaint,
                    llssoilfactor: llssoilfactor,
                    llsringgitfactor: llsringgitfactor,
                    mashospitaldetails: {
                        id: hospitalDetailsId,
                        hospitalname: hospitalname
                    },
                    id: primaryId
                };
                // POST request
                const response = await axios.post(`${MasterGraph}/api/mashospitaladdlinfos`, payload, config);
                const savedDataId = response.data.id;
                setPrimaryId(savedDataId); // Set the primaryId to the ID received from POST response
                setSuccessDialogMessage('Data saved successfully.');
            }
            // Open success dialog
            setSuccessDialogOpen(true);
        } catch (error) {
            console.error("Error saving Additional Info:", error);
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges && !isViewMode) {
            setCancelDialogOpen(true);
        } else {
            navigate(-1, { replace: true });
        }
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        navigate(-1, { replace: true });
    };



    useEffect(() => {
        if (hospitalDetailsId) {
            fetchAdditionalInfo();
        }
    }, [hospitalDetailsId]);

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleDialogConfirm = () => {
        setDialogOpen(false);
    };

    return (
        <TabContentLayout>
            <DefaultCard sx={{ height: '100%' }}>
                <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
                    <Box mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    label="Hospital Code"
                                    id="hospital-code"
                                    value={hospitalcode}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    label="Hospital Name"
                                    id="hospital-name"
                                    value={hospitalname}
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={9}
                                    label="Land Area (acre)"
                                    id="land-area"
                                    value={landarea}
                                    onChange={(newValue) => handleChange('landarea', newValue)}
                                    disabled={isViewMode}
                                    error={errors.landarea}
                                    errorMessage={errorMessages.landarea}
                                    autoFocus={true}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={9}
                                    label="CLS Area (sq.ft)"
                                    id="cls-area"
                                    value={clsarea}
                                    onChange={(newValue) => handleChange('clsarea', newValue)}
                                    disabled={isViewMode}
                                    error={errors.clsarea}
                                    errorMessage={errorMessages.clsarea}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={4}
                                    label="Bed Strength"
                                    id="bed-strength"
                                    value={bedstrength}
                                    onChange={(newValue) => handleChange('bedstrength', newValue)}
                                    disabled={isViewMode}
                                    error={errors.bedstrength}
                                    errorMessage={errorMessages.bedstrength}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={9}
                                    label="Ground Maint. Area (m sq)"
                                    id="groundmaint"
                                    value={groundmaint}
                                    onChange={(newValue) => handleChange('groundmaint', newValue)}
                                    disabled={isViewMode}
                                    error={errors.groundmaint}
                                    errorMessage={errorMessages.groundmaint}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={9}
                                    label="LLS Soil Factor"
                                    id="lls-soil-factor"
                                    value={llssoilfactor}
                                    onChange={(newValue) => handleChange('llssoilfactor', newValue)}
                                    disabled={isViewMode}
                                    error={errors.llssoilfactor}
                                    errorMessage={errorMessages.llssoilfactor}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <CustomTextField
                                    maxlength={9}
                                    label="LLS Ringgit Factor"
                                    id="lls-ringgit-factor"
                                    value={llsringgitfactor}
                                    onChange={(newValue) => handleChange('llsringgitfactor', newValue)}
                                    disabled={isViewMode}
                                    error={errors.llsringgitfactor}
                                    errorMessage={errorMessages.llsringgitfactor}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </form>
            </DefaultCard>
            <CustomDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                title="Error"
                content="No Additional Info found"
                confirmBtn="OK"
                onConfirm={handleDialogConfirm}
            />
            <CustomDialog
                open={successDialogOpen}
                onClose={() => setSuccessDialogOpen(false)}
                content={succesDialogMessage}
                confirmBtn="OK"
                onConfirm={() => setSuccessDialogOpen(false)}
            />
            <CustomDialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                title="Unsaved Changes"
                content="Record(s) not Saved/Updated. Do you want to continue?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleCancelDialogConfirm}
                onCancel={handleCancelDialogClose}
            />
            {isViewMode ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                        Back
                    </Button>
                </Box>

            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="contained" color="primary" sx={{ m: 1 }} onClick={handleSubmit} >
                        Save
                    </Button>
                    <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                        Back
                    </Button>
                </Box>
            )}

        </TabContentLayout>
    );
}

export default AdditionalInfoContent;
